import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { ThemeProvider } from "@/components/theme-provider";
import { LanguageProvider } from "@/components/language-provider";
import { SafeAuthProvider } from "@/components/providers/SafeAuthProvider";
import { NannyThemeProvider } from "@/contexts/NannyThemeContext";
import { ReferralTrackingProvider } from "@/contexts/ReferralTrackingContext";
import { ReferralTrackingWrapper } from "@/components/providers/ReferralTrackingWrapper";
import { InterviewProvider } from "@/contexts/InterviewContext";
import { NotificationProvider } from "@/contexts/NotificationContext";
import React, { lazy, useEffect, Suspense } from 'react';
import '@/lib/i18n';

import { UnifiedLayout } from "./components/layout/UnifiedLayout";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { RobustErrorBoundary } from "@/components/ui/robust-error-boundary";
import { WorkspaceRoutes } from "./components/layout/WorkspaceRoutes";
import { PublicPageErrorBoundary } from "@/components/public/PublicPageErrorBoundary";
import { PublicJobBoardProvider } from "@/contexts/PublicJobBoardContext";
// Removed AuthGuard - PreRouteGate handles all auth decisions
import { PreRouteGate } from "@/components/routing/PreRouteGate";
import { SimpleRedirector } from "@/components/routing/SimpleRedirector";
import { ScrollToTop } from "@/components/routing/ScrollToTop";
// Direct imports for fast-loading account pages
import BillingPage from "./pages/account/BillingPage";
import RoleManagementPage from "./pages/account/RoleManagementPage";

// Lazy load all major pages for better performance
const EveeAI = lazy(() => import("./pages/general/EveeAI"));
const EveeAIAgent = lazy(() => import("./pages/general/EveeAIAgent"));
const EveeAIAgentJobCreationResult = lazy(() => import("./pages/general/EveeAIAgentJobCreationResult"));
const CalendarPage = lazy(() => import("./pages/CalendarPage"));
// Direct imports for instant loading of account pages
import SimpleProfile from "./pages/SimpleProfile";
import UserSettingsPage from "./pages/UserSettingsPage";

// Other pages - still lazy loaded
import Auth from "./pages/Auth"; // Eagerly loaded to prevent grey overlay
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const PublicProfile = lazy(() => import("./pages/PublicProfile"));
const CompanyJobListings = lazy(() => import("./pages/CompanyJobListings"));
const JobDetail = lazy(() => import("./pages/JobDetail"));
const CompanyPlatformProfile = lazy(() => import("./pages/CompanyPlatformProfile"));
const PrivacyPolicy = lazy(() => import("./pages/PrivacyPolicy"));
const TermsOfService = lazy(() => import("./pages/TermsOfService"));
const RecruiterReferralTokensPage = lazy(() => import("./pages/RecruiterReferralTokensPage"));
const NotificationsPage = lazy(() => import("./pages/NotificationsPage"));
const UserProfilePage = lazy(() => import("./pages/UserProfilePage"));

// Nanny platform pages
const NannyAuth = lazy(() => import("./pages/nanny-platform/NannyAuth"));
const MotherAuth = lazy(() => import("./pages/nanny-platform/MotherAuth"));
const NannyPlatformAuth = lazy(() => import("./pages/nanny-platform/NannyPlatformAuth"));
const NannyRedirect = lazy(() => import("./pages/nanny-platform/NannyRedirect"));
const MotherRedirect = lazy(() => import("./pages/nanny-platform/MotherRedirect"));

import { FullScreenLoader, GlobalLoadingProvider, InlineLoader } from "@/components/ui/loading";
import { RoutePreloader } from "@/components/ui/route-preloader";
import { SessionErrorBoundary } from "@/components/ui/session-error-boundary";
import { ResourceLoadingFallback } from "@/components/ui/resource-loading-fallback";
import { UnifiedLayoutSkeleton } from "@/components/layout/UnifiedLayoutSkeleton";
import { GenericPageSkeleton } from "@/components/skeletons/GenericPageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslation } from "@/hooks/useTranslation";
import { logger } from "@/services/loggingService";
import { debugConfig } from "@/utils/debugConfig";

// Export queryClient for use in other modules (e.g., auth context)
export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 60 * 1000, // 60 seconds - prevent unnecessary refetches
      refetchOnWindowFocus: false,
      refetchOnMount: false, // Don't refetch if data is fresh
    },
  },
});



const App = () => {
  const { t: tCommon } = useTranslation('common');
  
  // Simplified initialization
  useEffect(() => {
    logger.debug('App initializing', { component: 'App' });
    
    // Set debug level to critical for quieter console by default
    if (import.meta.env.DEV && !localStorage.getItem('debug:level')) {
      localStorage.setItem('debug:level', 'critical');
    }

    // Add global link interceptor with breadcrumb tracking
    const handleLinkClick = (event: Event) => {
      const target = event.target as HTMLElement;
      const link = target.closest('a');
      
      if (link && link.href) {
        const url = new URL(link.href);
        const currentUrl = new URL(window.location.href);
        
        // Only intercept internal links
        if (url.origin === currentUrl.origin && !link.target && !link.download) {
          if (debugConfig.isVerbose()) {
            logger.debug('Internal navigation', {
              component: 'App',
              metadata: { href: url.pathname, from: currentUrl.pathname }
            });
          }
        }
      }
    };

    document.addEventListener('click', handleLinkClick);
    
    return () => {
      document.removeEventListener('click', handleLinkClick);
    };
  }, []);

  return (
    <RobustErrorBoundary>
      <ErrorBoundary>
        <ResourceLoadingFallback resourceName="application">
          <QueryClientProvider client={queryClient}>
              <ThemeProvider defaultTheme="system" storageKey="vite-ui-theme">
                <LanguageProvider>
                  <SafeAuthProvider>
                    <NotificationProvider>
                    <ReferralTrackingProvider>
                      <GlobalLoadingProvider>
                    <SessionErrorBoundary>
                      <TooltipProvider>
                  <Toaster position="top-right" />
                  <div className="h-svh max-h-svh overflow-hidden">
                    <BrowserRouter>
                      <NannyThemeProvider>
                      <ScrollToTop />
                      <InterviewProvider>
                        <ReferralTrackingWrapper>
                          <RoutePreloader />
                          <PreRouteGate>
                        <Routes>
                          {/* Public routes - no authentication required */}
                          <Route path="/auth" element={<Auth />} />
                          
                          {/* Nanny/Mother Platform Routes */}
                          <Route path="/nanny-platform-auth" element={
                            <Suspense fallback={<FullScreenLoader variant="with-text" text="Loading..." />}>
                              <NannyPlatformAuth />
                            </Suspense>
                          } />
                          
                          {/* Smart redirects for clean URLs */}
                          <Route path="/nanny" element={
                            <Suspense fallback={<FullScreenLoader variant="with-text" text="Loading Nanny Platform..." />}>
                              <NannyRedirect />
                            </Suspense>
                          } />
                          <Route path="/mother" element={
                            <Suspense fallback={<FullScreenLoader variant="with-text" text="Loading Parent Platform..." />}>
                              <MotherRedirect />
                            </Suspense>
                          } />
                          
                          {/* Backward compatibility - redirect to unified auth */}
                          <Route path="/nanny-auth" element={
                            <Suspense fallback={<FullScreenLoader variant="with-text" text="Loading..." />}>
                              <NannyAuth />
                            </Suspense>
                          } />
                          <Route path="/mother-auth" element={
                            <Suspense fallback={<FullScreenLoader variant="with-text" text="Loading..." />}>
                              <MotherAuth />
                            </Suspense>
                          } />
                          <Route path="/reset-password" element={
                        <Suspense fallback={<FullScreenLoader variant="with-text" text={tCommon('loading_reset_password')} />}>
                          <ResetPassword />
                        </Suspense>
                          } />
                          <Route path="/" element={<SimpleRedirector />} />
                          
                          {/* Demo routes - no authentication required */}
                          <Route path="/demo" element={
                            <Suspense fallback={<FullScreenLoader variant="with-text" text="Loading Demo..." />}>
                              {React.createElement(lazy(() => import("./pages/DemoOverview")))}
                            </Suspense>
                          } />
                          <Route path="/demo/interview" element={
                            <Suspense fallback={<FullScreenLoader variant="with-text" text="Loading Demo Interview..." />}>
                              {React.createElement(lazy(() => import("./pages/DemoInterview")))}
                            </Suspense>
                          } />
                          <Route path="/demo/candidate-review" element={
                            <Suspense fallback={<FullScreenLoader variant="with-text" text="Loading Demo..." />}>
                              {React.createElement(lazy(() => import("./pages/DemoCandidateReview")))}
                            </Suspense>
                          } />
                          
          {/* Short code routes (new - ~70% shorter URLs) */}
          <Route path="/c/:companyIdentifier" element={
            <PublicJobBoardProvider>
              <PublicPageErrorBoundary>
                <Suspense fallback={
                  <div className="max-w-4xl mx-auto px-4 py-6">
                    <Skeleton className="h-20 w-20 rounded-xl mx-auto mb-6" />
                    <Skeleton className="h-8 w-48 mx-auto mb-4" />
                  </div>
                }>
                  <CompanyJobListings />
                </Suspense>
              </PublicPageErrorBoundary>
            </PublicJobBoardProvider>
          } />
          <Route path="/c/:companyIdentifier/:jobIdentifier" element={
            <PublicJobBoardProvider>
              <PublicPageErrorBoundary>
                <Suspense fallback={
                  <div className="max-w-4xl mx-auto px-4 py-6">
                    <Skeleton className="h-8 w-32 mb-6" />
                    <Skeleton className="h-10 w-64 mx-auto mb-4" />
                  </div>
                }>
                  <JobDetail />
                </Suspense>
              </PublicPageErrorBoundary>
            </PublicJobBoardProvider>
          } />
                          
                          {/* Legacy routes (backward compatibility) */}
          <Route path="/company/:companyIdentifier" element={
            <PublicJobBoardProvider>
              <PublicPageErrorBoundary>
                <Suspense fallback={
                  <div className="max-w-4xl mx-auto px-4 py-6">
                    <Skeleton className="h-20 w-20 rounded-xl mx-auto mb-6" />
                    <Skeleton className="h-8 w-48 mx-auto mb-4" />
                  </div>
                }>
                  <CompanyJobListings />
                </Suspense>
              </PublicPageErrorBoundary>
            </PublicJobBoardProvider>
          } />
          <Route path="/company/:companyIdentifier/:jobIdentifier" element={
            <PublicJobBoardProvider>
              <PublicPageErrorBoundary>
                <Suspense fallback={
                  <div className="max-w-4xl mx-auto px-4 py-6">
                    <Skeleton className="h-8 w-32 mb-6" />
                    <Skeleton className="h-10 w-64 mx-auto mb-4" />
                  </div>
                }>
                  <JobDetail />
                </Suspense>
              </PublicPageErrorBoundary>
            </PublicJobBoardProvider>
          } />
                          <Route path="/company-profile/:companyIdentifier" element={
                            <UnifiedLayout>
                              <Suspense fallback={<GenericPageSkeleton showMetrics contentCards={4} />}>
                                <CompanyPlatformProfile />
                              </Suspense>
                            </UnifiedLayout>
                          } />
                          <Route path="/privacy-policy" element={
                            <Suspense fallback={<FullScreenLoader variant="basic" />}>
                              <PrivacyPolicy />
                            </Suspense>
                          } />
                          <Route path="/terms-of-service" element={
                            <Suspense fallback={<FullScreenLoader variant="basic" />}>
                              <TermsOfService />
                            </Suspense>
                          } />
                          
                          {/* Protected routes - PreRouteGate handles auth requirements */}
                          <Route path="/user/:userId" element={
                            <UnifiedLayout>
                              <Suspense fallback={<GenericPageSkeleton showMetrics contentCards={3} />}>
                                <PublicProfile />
                              </Suspense>
                            </UnifiedLayout>
                          } />
                          
          <Route path="/evee-ai" element={
            <UnifiedLayout>
              <Suspense fallback={<GenericPageSkeleton contentCards={4} />}>
                <EveeAI />
              </Suspense>
            </UnifiedLayout>
          } />
          
          <Route path="/evee-ai-agent" element={
            <UnifiedLayout>
              <Suspense fallback={<GenericPageSkeleton contentCards={4} />}>
                <EveeAIAgent />
              </Suspense>
            </UnifiedLayout>
          } />
          
          <Route path="/evee-ai-agent-job-creation-result" element={
            <UnifiedLayout>
              <Suspense fallback={<UnifiedLayoutSkeleton />}>
                <EveeAIAgentJobCreationResult />
              </Suspense>
            </UnifiedLayout>
          } />
                          
                          <Route path="/calendar" element={
                            <UnifiedLayout>
                              <Suspense fallback={<GenericPageSkeleton contentCards={1} />}>
                                <CalendarPage />
                              </Suspense>
                            </UnifiedLayout>
                          } />
                          
                          <Route path="/notifications" element={
                            <UnifiedLayout>
                              <Suspense fallback={<GenericPageSkeleton contentCards={5} />}>
                                <NotificationsPage />
                              </Suspense>
                            </UnifiedLayout>
                          } />
                          
                          <Route path="/workspace/recruiter/referrals" element={
                            <UnifiedLayout>
                              <Suspense fallback={<GenericPageSkeleton showMetrics contentCards={4} />}>
                                <RecruiterReferralTokensPage />
                              </Suspense>
                            </UnifiedLayout>
                          } />
                           
                           <Route path="/workspace/user/:userId" element={
                             <UnifiedLayout>
                               <Suspense fallback={<GenericPageSkeleton showMetrics contentCards={3} />}>
                                 <UserProfilePage />
                               </Suspense>
                             </UnifiedLayout>
                           } />
                           
                           {/* Payment routes */}
                            <Route path="/payment/success" element={
                          <Suspense fallback={<FullScreenLoader variant="with-text" text={tCommon('loading_payment_confirmation')} />}>
                            {React.createElement(lazy(() => import("./pages/payment").then(m => ({ default: m.PaymentSuccess }))))}
                          </Suspense>
                            } />
                            <Route path="/payment/cancel" element={
                          <Suspense fallback={<FullScreenLoader variant="with-text" text={tCommon('loading')} />}>
                            {React.createElement(lazy(() => import("./pages/payment").then(m => ({ default: m.PaymentCancel }))))}
                          </Suspense>
                            } />
                          
                          
                          <Route path="/workspace/*" element={
                            <UnifiedLayout>
                              <WorkspaceRoutes />
                            </UnifiedLayout>
                          } />
                          
                          <Route path="/account/*" element={
                            <UnifiedLayout>
                             <Routes>
                               <Route path="/profile" element={<SimpleProfile />} />
                               <Route path="/billing" element={<BillingPage />} />
                               <Route path="/settings" element={<UserSettingsPage />} />
                                <Route path="/roles" element={<RoleManagementPage />} />
                                 <Route path="*" element={<div className="h-full overflow-y-auto flex items-center justify-center"><div className="text-center"><h1 className="text-4xl font-bold mb-4">404</h1><p className="text-xl text-muted-foreground mb-4">{tCommon('error_page_not_found')}</p></div></div>} />
                              </Routes>
                            </UnifiedLayout>
                          } />
                          
                          <Route path="*" element={<div className="h-full overflow-y-auto flex items-center justify-center"><div className="text-center"><h1 className="text-4xl font-bold mb-4">404</h1><p className="text-xl text-muted-foreground mb-4">{tCommon('error_page_not_found')}</p></div></div>} />
                        </Routes>
                        </PreRouteGate>
                        </ReferralTrackingWrapper>
                      </InterviewProvider>
                      </NannyThemeProvider>
                    </BrowserRouter>
                    </div>
                      </TooltipProvider>
                    </SessionErrorBoundary>
                      </GlobalLoadingProvider>
                    </ReferralTrackingProvider>
                    </NotificationProvider>
                  </SafeAuthProvider>
                </LanguageProvider>
              </ThemeProvider>
          
        </QueryClientProvider>
      </ResourceLoadingFallback>
    </ErrorBoundary>
  </RobustErrorBoundary>
);
};

export default App;
